import { useCallback, useEffect, useRef, useState } from 'react'

interface WordSelection {
  text: string
  rect: DOMRect
}

interface UseWordSelectionReturn {
  selection: WordSelection | null
  clearSelection: () => void
}

// How many pixels the pointer must move before we consider it a drag (not a click/tap)
const DRAG_THRESHOLD = 8

export function useWordSelection(
  containerRef: React.RefObject<HTMLDivElement | null>,
): UseWordSelectionReturn {
  const [selection, setSelection] = useState<WordSelection | null>(null)

  const startEl = useRef<HTMLElement | null>(null)
  const isDragging = useRef(false)
  const startPos = useRef<{ x: number; y: number } | null>(null)

  // Touch: require a long press before word selection activates (so scrolling works)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selectionReady = useRef(false)

  const clearHighlights = useCallback(() => {
    containerRef.current
      ?.querySelectorAll('.word-selected')
      .forEach((el) => el.classList.remove('word-selected'))
  }, [containerRef])

  const clearSelection = useCallback(() => {
    clearHighlights()
    setSelection(null)
  }, [clearHighlights])

  const highlightRange = useCallback(
    (start: HTMLElement, end: HTMLElement) => {
      const container = containerRef.current
      if (!container) return

      const allWords = Array.from(container.querySelectorAll<HTMLElement>('.word-token'))
      const si = allWords.indexOf(start)
      const ei = allWords.indexOf(end)
      if (si === -1 || ei === -1) return

      const [from, to] = si <= ei ? [si, ei] : [ei, si]
      allWords.forEach((el) => el.classList.remove('word-selected'))
      for (let i = from; i <= to; i++) allWords[i].classList.add('word-selected')
    },
    [containerRef],
  )

  const finalizeSelection = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const selected = Array.from(container.querySelectorAll<HTMLElement>('.word-selected'))
    if (selected.length < 2) {
      clearHighlights()
      return
    }

    const text = selected.map((el) => el.textContent ?? '').join(' ')
    const rects = selected.map((el) => el.getBoundingClientRect())
    const top = Math.min(...rects.map((r) => r.top))
    const bottom = Math.max(...rects.map((r) => r.bottom))
    const left = Math.min(...rects.map((r) => r.left))
    const right = Math.max(...rects.map((r) => r.right))

    setSelection({ text, rect: new DOMRect(left, top, right - left, bottom - top) })
  }, [containerRef, clearHighlights])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    function nearestWord(x: number, y: number): HTMLElement | null {
      const el = document.elementFromPoint(x, y)
      return (el?.closest('.word-token') ?? null) as HTMLElement | null
    }

    function wordFromTarget(target: EventTarget | null): HTMLElement | null {
      return ((target as HTMLElement)?.closest('.word-token') ?? null) as HTMLElement | null
    }

    // ── MOUSE ──────────────────────────────────────────────────────────────

    function onMouseDown(e: MouseEvent) {
      if (e.button !== 0) return
      // Clear previous selection but don't stomp the click — the click event
      // fires after mouseup and will open the word popover if no drag happened.
      clearHighlights()
      setSelection(null)
      startPos.current = { x: e.clientX, y: e.clientY }
      startEl.current = wordFromTarget(e.target)
      isDragging.current = false
    }

    function onMouseMove(e: MouseEvent) {
      if (!(e.buttons & 1) || !startPos.current) return

      const dx = e.clientX - startPos.current.x
      const dy = e.clientY - startPos.current.y
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return

      isDragging.current = true
      const wordEl = nearestWord(e.clientX, e.clientY)
      if (!wordEl) return

      // If drag started on whitespace, adopt the first word reached as the anchor
      if (!startEl.current) startEl.current = wordEl
      highlightRange(startEl.current, wordEl)
    }

    function onMouseUp() {
      if (isDragging.current) finalizeSelection()
      startPos.current = null
      startEl.current = null
      isDragging.current = false
    }

    // ── TOUCH (long-press to select, normal touch scrolls) ──────────────

    const HOLD_DELAY = 400

    function onTouchStart(e: TouchEvent) {
      const t = e.touches[0]
      clearHighlights()
      setSelection(null)
      startPos.current = { x: t.clientX, y: t.clientY }
      startEl.current = wordFromTarget(e.target)
      isDragging.current = false
      selectionReady.current = false

      if (holdTimer.current) clearTimeout(holdTimer.current)
      holdTimer.current = setTimeout(() => {
        selectionReady.current = true
        holdTimer.current = null
      }, HOLD_DELAY)
    }

    function onTouchMove(e: TouchEvent) {
      const t = e.touches[0]
      if (!startPos.current) return

      const dx = t.clientX - startPos.current.x
      const dy = t.clientY - startPos.current.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      // Finger moved before hold completed — cancel selection, allow scroll
      if (!selectionReady.current) {
        if (dist >= DRAG_THRESHOLD && holdTimer.current) {
          clearTimeout(holdTimer.current)
          holdTimer.current = null
          startPos.current = null
        }
        return
      }

      // Long press completed — selection mode active
      if (dist < DRAG_THRESHOLD) return

      isDragging.current = true
      e.preventDefault() // prevent page scroll only during active selection

      const wordEl = nearestWord(t.clientX, t.clientY)
      if (!wordEl) return
      if (!startEl.current) startEl.current = wordEl
      highlightRange(startEl.current, wordEl)
    }

    function onTouchEnd(e: TouchEvent) {
      if (holdTimer.current) {
        clearTimeout(holdTimer.current)
        holdTimer.current = null
      }

      if (isDragging.current) {
        e.preventDefault() // prevent the synthetic click that follows
        finalizeSelection()
      }

      startPos.current = null
      startEl.current = null
      isDragging.current = false
      selectionReady.current = false
    }

    // ── PREVENT NATIVE SELECTION ───────────────────────────────────────────

    function onSelectStart(e: Event) { e.preventDefault() }
    function onContextMenu(e: Event) { e.preventDefault() }

    // mouse listeners: mousedown on container, move/up on document
    container.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    container.addEventListener('touchstart', onTouchStart, { passive: true })
    container.addEventListener('touchmove', onTouchMove, { passive: false })
    container.addEventListener('touchend', onTouchEnd)

    container.addEventListener('selectstart', onSelectStart)
    container.addEventListener('contextmenu', onContextMenu)

    return () => {
      container.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)

      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', onTouchEnd)

      container.removeEventListener('selectstart', onSelectStart)
      container.removeEventListener('contextmenu', onContextMenu)
    }
  }, [containerRef, clearHighlights, highlightRange, finalizeSelection])

  return { selection, clearSelection }
}
